import { useEffect, useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { toast } from 'sonner';
import { api } from '../../api/client';
import { extractErr } from '../../api/error-message';
import { CardHeader } from '../../components/card';
import { DataTable, type Column } from '../../components/data-table';
import { ChartEmpty } from '../../components/empty-state';
import { Skeleton } from '../../components/skeleton';
import { StatCard } from '../../components/stat-card';
import { IndianRupee, Percent, Store, Truck, Wallet } from '../../components/icons';
import {
  CHART, SPLIT_COLORS, SplitBar, defaultRange, inr, inr2, periodLabel, todayIst,
  type Granularity, type Split,
} from './shared';

interface Bucket extends Split { period: string; orders: number; items: number }
interface Revenue {
  from: string;
  to: string;
  granularity: Granularity;
  totals: Split & { orders: number; items: number };
  buckets: Bucket[];
}

const GRANULARITIES: { key: Granularity; label: string }[] = [
  { key: 'day', label: 'Day' },
  { key: 'week', label: 'Week' },
  { key: 'month', label: 'Month' },
];

export function RevenueTab() {
  const [g, setG] = useState<Granularity>('day');
  const [range, setRange] = useState(() => defaultRange('day'));
  const [r, setR] = useState<Revenue | null>(null);
  const [loading, setLoading] = useState(true);

  // Switching Day/Week/Month resets to that view's natural window.
  const pickGranularity = (next: Granularity) => { setG(next); setRange(defaultRange(next)); };

  useEffect(() => {
    if (range.from > range.to) return;
    let alive = true;
    setLoading(true);
    api
      .get<Revenue>('/admin/analytics/revenue', { params: { ...range, granularity: g } })
      .then(({ data }) => { if (alive) setR(data); })
      .catch((err) => { if (alive) toast.error(extractErr(err)); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [g, range]);

  const t = r?.totals;
  const lastIndex = (r?.buckets.length ?? 0) - 1;
  const hasData = Boolean(t && t.customerPaid !== 0);

  const columns: Column<Bucket>[] = [
    { key: 'period', label: 'Period', primary: true, render: (b) => periodLabel(b.period, g, true) },
    { key: 'orders', label: 'Orders', align: 'right', render: (b) => <span className="font-tabular">{b.orders}</span> },
    { key: 'customerPaid', label: 'Customer paid', align: 'right', render: (b) => <span className="font-tabular">{inr2(b.customerPaid)}</span> },
    { key: 'sellerShare', label: 'Seller', align: 'right', render: (b) => <span className="font-tabular">{inr2(b.sellerShare)}</span> },
    { key: 'partnerShare', label: 'Delivery partner', align: 'right', render: (b) => <span className="font-tabular">{inr2(b.partnerShare)}</span> },
    { key: 'tax', label: 'GST', align: 'right', render: (b) => <span className="font-tabular">{inr2(b.tax)}</span> },
    {
      key: 'companyRevenue', label: 'Company revenue', align: 'right',
      render: (b) => <span className="font-tabular font-semibold text-ink-900">{inr2(b.companyRevenue)}</span>,
    },
  ];

  return (
    <div className="space-y-6">
      {/* Filters — one row above everything they drive. */}
      <div className="flex flex-wrap items-end gap-3">
        <div className="seg" role="group" aria-label="Group by">
          {GRANULARITIES.map((x) => (
            <button key={x.key} type="button" onClick={() => pickGranularity(x.key)} aria-pressed={g === x.key}>
              {x.label}
            </button>
          ))}
        </div>
        <label className="text-[13px] text-ink-500">
          From
          <input
            type="date" className="input mt-1 block" value={range.from} max={range.to}
            onChange={(e) => e.target.value && setRange((x) => ({ ...x, from: e.target.value }))}
          />
        </label>
        <label className="text-[13px] text-ink-500">
          To
          <input
            type="date" className="input mt-1 block" value={range.to} min={range.from} max={todayIst()}
            onChange={(e) => e.target.value && setRange((x) => ({ ...x, to: e.target.value }))}
          />
        </label>
      </div>

      {loading || !r || !t ? (
        <div role="status" aria-busy="true" aria-label="Loading" className="space-y-5">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-5">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="panel p-[18px]">
                <Skeleton className="h-4 w-28" />
                <Skeleton className="mt-4 h-8 w-36" />
              </div>
            ))}
          </div>
          <Skeleton className="h-72 w-full" />
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-5">
            <StatCard icon={<IndianRupee size={18} />} label="Customers paid" value={inr(t.customerPaid)} subValue={`${t.orders} delivered orders`} />
            <StatCard
              icon={<Wallet size={18} />} label="Company revenue" value={inr(t.companyRevenue)}
              subValue={t.customerPaid > 0 ? `${((t.companyRevenue / t.customerPaid) * 100).toFixed(1)}% of customer payments` : undefined}
            />
            <StatCard icon={<Store size={18} />} label="Seller share" value={inr(t.sellerShare)} />
            <StatCard icon={<Truck size={18} />} label="Delivery partner share" value={inr(t.partnerShare)} />
            <StatCard icon={<Percent size={18} />} label="GST collected" value={inr(t.tax)} subValue="Payable to government" />
          </div>

          <div className="grid grid-cols-1 gap-5 lg:grid-cols-[1.6fr_1fr]">
            <div className="panel p-5">
              <CardHeader
                title={`Company revenue by ${g}`}
                description={`${periodLabel(r.from, 'day', true)} – ${periodLabel(r.to, 'day', true)}`}
              />
              <div className="h-72">
                {!hasData ? (
                  <ChartEmpty title="No delivered orders in this period" body="Revenue is counted once an order item is marked delivered." />
                ) : (
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={r.buckets} margin={{ top: 4, right: 4, left: 0, bottom: 0 }}>
                      <CartesianGrid stroke={CHART.line} vertical={false} />
                      <XAxis
                        dataKey="period"
                        tick={{ fill: CHART.axis, fontSize: 12 }}
                        tickFormatter={(v: string) => periodLabel(v, g)}
                        axisLine={{ stroke: CHART.line }}
                        tickLine={false}
                        minTickGap={12}
                      />
                      <YAxis
                        tick={{ fill: CHART.axis, fontSize: 12 }} axisLine={false} tickLine={false} width={64}
                        tickFormatter={(v: number) => inr(v)}
                      />
                      <Tooltip
                        cursor={{ fill: CHART.hover }}
                        content={({ active, payload }) => {
                          const b = active && payload?.[0]?.payload as Bucket | undefined;
                          if (!b) return null;
                          return (
                            <div className="rounded-[12px] border border-ink-200 bg-white p-3 text-[13px] shadow-sm">
                              <p className="mb-1.5 font-semibold text-ink-900">{periodLabel(b.period, g, true)}</p>
                              <TipRow label="Company revenue" value={b.companyRevenue} color={SPLIT_COLORS.company} strong />
                              <TipRow label="Seller" value={b.sellerShare} color={SPLIT_COLORS.seller} />
                              <TipRow label="Delivery partner" value={b.partnerShare} color={SPLIT_COLORS.partner} />
                              <TipRow label="GST" value={b.tax} color={SPLIT_COLORS.tax} />
                              <p className="mt-1.5 border-t border-ink-200 pt-1.5 text-ink-500">
                                Customers paid <span className="font-tabular text-ink-900">{inr2(b.customerPaid)}</span> · {b.orders} orders
                              </p>
                            </div>
                          );
                        }}
                      />
                      <Bar dataKey="companyRevenue" radius={[4, 4, 0, 0]} maxBarSize={40} fill={SPLIT_COLORS.company}>
                        {r.buckets.map((_, i) => (
                          // Current period at full strength, history at 55%.
                          <Cell key={i} fill={SPLIT_COLORS.company} fillOpacity={i === lastIndex ? 1 : 0.55} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </div>
            </div>

            <div className="panel p-5">
              <CardHeader title="Where customer payments went" />
              <SplitBar split={t} />
              <CardHeader title="Company revenue made up of" className="mb-2 mt-6" />
              <dl className="space-y-1.5 text-[14px]">
                <Line label="Commission & seller platform fee" value={t.companyBreakdown.commissionAndFees} />
                <Line label="Price markup (product + delivery fee %)" value={t.companyBreakdown.priceMarkup} />
                <Line label="Delivery charges paid by customers" value={t.companyBreakdown.deliveryCharges} />
                <Line label="Discounts given" value={-t.companyBreakdown.discounts} />
                <Line label="Paid to delivery partners" value={-t.companyBreakdown.partnerPayouts} />
                <div className="flex justify-between border-t border-ink-200 pt-2 font-semibold text-ink-900">
                  <dt>Company revenue</dt>
                  <dd className="font-tabular">{inr2(t.companyRevenue)}</dd>
                </div>
              </dl>
            </div>
          </div>

          <DataTable
            columns={columns}
            data={[...r.buckets].reverse()}
            rowKey={(b) => b.period}
            searchPlaceholder="Search periods…"
            searchFields={(b) => [periodLabel(b.period, g, true)]}
            exportName={`revenue-by-${g}-${r.from}-to-${r.to}`}
          />
        </>
      )}
    </div>
  );
}

function TipRow({ label, value, color, strong = false }: { label: string; value: number; color: string; strong?: boolean }) {
  return (
    <p className="flex items-center justify-between gap-6">
      <span className="flex items-center gap-1.5 text-ink-500">
        <span className="h-2 w-2 rounded-[2px]" style={{ background: color }} aria-hidden />
        {label}
      </span>
      <span className={`font-tabular ${strong ? 'font-semibold text-ink-900' : 'text-ink-700'}`}>{inr2(value)}</span>
    </p>
  );
}

function Line({ label, value }: { label: string; value: number }) {
  return (
    <div className="flex justify-between gap-4 text-ink-700">
      <dt>{label}</dt>
      <dd className="font-tabular">{value < 0 ? `− ${inr2(-value)}` : inr2(value)}</dd>
    </div>
  );
}
